<template>
  <div class="measurements-menu" :class="{ mobile: device === 'mobile' }">
    <div class="left half">
      <div class="heading">{{ $t('cylinder.measurements.measurements') }}</div>
      <attachment-scheme></attachment-scheme>
      <button
        class="download btn"
        type="button"
        @click="downloadPDF"
        :class="{disabled: !this.selectedCilinder}"
      >{{ $t('cylinder.measurements.pdfDownload') }}</button>
      <div class="alert-danger error" v-if="error">
        <span>{{error}}</span>
      </div>
    </div>
    <div class="right half">
      <div class="heading">{{ $t('cylinder.measurements.pushForce') }}</div>
      <div class="push-specs">
        <div class="spec">
          <img :src="getImage('cilinder/clock.svg')">
          <span class="spec-value">{{ getBoreSpeed }} mm/s</span>
        </div>
        <div class="spec">
          <img :src="getImage('cilinder/clock.svg')">
          <span class="spec-value">{{
              (getTotalTimeBore === 'Infinity' || getTotalTimeBore === '-Infinity') ? (0).toFixed(2) : getTotalTimeBore
            }} s</span>
        </div>
        <div class="spec">
          <img :src="getImage('meter.svg')">
          <span class="spec-value">{{ getPushLiters }} l</span>
        </div>
        <div class="spec">
          <img :src="getImage('cilinder/push.svg')">
          <span class="spec-value">
            {{ round(
              getForce(cilinderOptions.pressure, cilinderOptions.circuit === 1 ? getBoreArea : getRodArea, forceType)
          ) }} {{ forceType }}
          </span>
        </div>
      </div>
      <div class="cilinder-img">
        <img class="cilinder-render" :src="cilinderPushImage">
        <img class="arrow right" src="../../assets/cilinder/arrow-right.png">
      </div>

      <div class="heading" v-if="cilinderOptions.circuit === 1">{{ $t('cylinder.measurements.pullForce') }}</div>
      <div class="pull-specs" v-if="cilinderOptions.circuit === 1">
        <div class="spec">
          <img :src="getImage('cilinder/clock.svg')">
          <span class="spec-value">{{ getRodSpeed.toFixed(2) }} mm/s</span>
        </div>
        <div class="spec">
          <img :src="getImage('cilinder/clock.svg')">
          <span class="spec-value">{{ getTotalTimeRod.toFixed(2) }} s</span>
        </div>
        <div class="spec">
          <img :src="getImage('meter.svg')">
          <span class="spec-value">{{ getPullLiters.toFixed(2) }} l</span>
        </div>
        <div class="spec">
          <img :src="getImage('cilinder/pull.svg')">
          <span class="spec-value">
            {{ round(getForce(cilinderOptions.pressure, getBoreArea - getRodArea, forceType)) }} {{ forceType }}
          </span>
        </div>
      </div>
      <div class="cilinder-img" v-if="cilinderOptions.circuit === 1">
        <img class="cilinder-render" :src="cilinderPullImage || '../../assets/cilinder/piston-pull.png'">
        <img class="arrow left" src="../../assets/cilinder/arrow-right.png">
      </div>
    </div>
  </div>
</template>

<script>
import MenuItem from "../step2/menu/MenuItem";
import {mapActions, mapGetters, mapState} from 'vuex';
import axios from "axios";

import {getForce, round} from "@/helpers/calculations";
import AttachmentScheme from "@/components/cilinder/AttachmentScheme";
import FileDownload from "js-file-download";
import Swal from "sweetalert2";
import { getFromStorage } from "@/helpers/backendStorage";

export default {
  name: "MeasurementsMenu",
  components: {AttachmentScheme, MenuItem},
  data() {
    return {
      error: null,
    }
  },

  computed: {
    ...mapState({
      magentoLoggedIn: state => state.preference.magentoLoggedIn,
      selectedCilinder: state => state.preference.selectedCilinder,
    }),
    ...mapGetters({
      cilinderOptions: 'preference/getCilinderOptions',
      cilinder: 'preference/getSelectedCilinder',
      attachmentsById: 'cilinders/getAttachmentsById',
      cilinderCircuit: 'preference/getCilinderCircuit',
      user: 'auth/getUser',
      device: 'getDevice',
      isLoggedIn: 'auth/isLoggedIn',
      isMagento: 'preference/isMagento',
    }),
    forceType() {
      if (this.cilinderOptions.forceUnit === 1) {
        return 'kg';
      } else if (this.cilinderOptions.forceUnit === 2) {
        return 'kN';
      }
    },
    isSingleActing() {
      return this.cilinderCircuit === 'single'
    },
    cilinderPullImage() {
      let type = this.cilinderOptions.type === 0 ? 1 : this.cilinderOptions.type;

      if (this.cilinder) {
        type = this.cilinder.attachment_id;

        return getFromStorage(this.attachmentsById[type]['image_pull']);
      }

      if (!this.cilinderOptions || !this.attachmentsById[type] || this.isSingleActing) return false;

      return getFromStorage(this.attachmentsById[type]['image_pull']);
    },
    cilinderPushImage() {
      let type = this.cilinderOptions.type === 0 ? 1 : this.cilinderOptions.type;
      let imageName = this.isSingleActing ? 'image_single_push' : 'image_push'

      if (this.cilinder) {
        type = this.cilinder.attachment_id;
        return getFromStorage(this.attachmentsById[type][imageName]);
      }
      if (!this.cilinderOptions || !this.attachmentsById[type]) return false;

      return getFromStorage(this.attachmentsById[type][imageName]);
    },
    getBoreArea() {
      return Math.pow(this.cilinderOptions.pistonDiameter / 2, 2) * Math.PI / 100;
    },
    getRodAreaCalculation() {
      return this.getBoreArea - (Math.pow(this.cilinderOptions.rodDiameter / 2, 2) * Math.PI / 100);
    },
    getRodArea() {
      return Math.pow(this.cilinderOptions.rodDiameter / 2, 2) * Math.PI / 100;
    },
    getBoreVolume() {
      return ((+this.cilinderOptions.stroke / 10) * this.getBoreArea) / 1000;
    },
    getRodVolume() {
      return ((+this.cilinderOptions.stroke / 10) * this.getRodArea) / 1000;
    },
    getBoreForce() {
      return this.getBoreArea * this.pressure;
    },
    getRodForce() {
      return this.getRodArea * this.pressure;
    },
    getBoreStroke() {
      return +this.getBoreVolume / (this.oilFlow / 60);
    },
    getRodStroke() {
      return +this.getRodVolume / (this.oilFlow / 60);
    },
    getBoreVelocity() {
      return this.stroke / this.getBoreStroke;
    },
    getRodVelocity() {
      return this.stroke / this.getRodStroke;
    },
    getZ() {
      if (this.cilinder) {
        return +this.cilinder.stroke_k + +this.cilinder.plus_waarde;
      }
    },
    getRodSpeed() {
      if (!this.getTotalTimeRod) {
        return 0;
      }

      return (+this.cilinderOptions.stroke ?? 0) / this.getTotalTimeRod;
    },
    getBoreSpeed() {
      if (this.getBoreArea) {
        return (100 / ((((this.getBoreArea * 100 / 10) / 1000) / this.cilinderOptions.oilFlow) * 60)).toFixed(2);
      }
      return (0).toFixed(2);
    },
    getTotalTimeRod() {
      if (!this.getPullLiters || !(+this.cilinderOptions.oilFlow)) {
        return 0;
      }

      return 60 / (this.cilinderOptions.oilFlow / this.getPullLiters);
    },
    getTotalTimeBore() {
      if (this.getBoreVolume) {
        return (this.getBoreVolume / (this.cilinderOptions.oilFlow / 60)).toFixed(2);
      }
      return (0).toFixed(2);
    },
    getPushLiters() {
      const boring = +this.cilinderOptions.pistonDiameter;
      const stroke = +this.cilinderOptions.stroke;

      return (
          ((boring * boring * Math.PI / 4) * stroke) / 1000000
      ).toFixed(2);
    },
    getPullLiters() {
      const boring = +this.cilinderOptions.pistonDiameter;
      const rod = +this.cilinderOptions.rodDiameter;
      const stroke = +this.cilinderOptions.stroke;

      return (
          (((boring * boring * Math.PI / 4) * stroke) - ((rod * rod * Math.PI / 4) * stroke)) / 1000000
      );
    }
  },

  methods: {
    ...mapActions({
      redirectToLoginUrl: 'auth/redirectToLoginUrl',
    }),
    getForce,
    round,
    openAid(type, tab) {
      this.$store.commit('preference/openAid', {type, tab});
    },

    downloadPDF() {
      this.error = null;
      if (!this.selectedCilinder) {
        this.error = 'Selecteer eerst een cilinder';
        return;
      }

      axios.post(process.env.VUE_APP_API_URL + 'cilinder/pdf', {
        cilinder: this.cilinder,
        user: this.user,
        preferences: {
          forceType: this.forceType,
          pressure: this.cilinderOptions.pressure,
          oilFlow: this.cilinderOptions.oilFlow,
        },
        calculations: {
          boreArea: this.getBoreArea,
          rodArea: this.getBoreArea - this.getRodArea,
          boreVolume: this.getBoreVolume,
          rodVolume: this.getPullLiters,
          pushBoreSpeed: +this.getBoreSpeed,
          pushTotalTimeBore: (this.getTotalTimeBore === 'Infinity' || this.getTotalTimeBore === '-Infinity') ? 0.00 : this.getTotalTimeBore,
          pushForce: this.getForce(this.cilinderOptions.pressure, this.getBoreArea, this.forceType),
          pullRodSpeed: +this.getRodSpeed,
          pullTotalTimeRod: (this.getTotalTimeRod === 'Infinity' || this.getTotalTimeRod === '-Infinity') ? 0.00 : this.getTotalTimeRod,
          pullForce: this.getForce(this.cilinderOptions.pressure, this.getBoreArea - this.getRodArea, this.forceType),
        }
      })
          .then((response) => {
            this.$store.dispatch('share/open', {
              configurator: 'cylinder',
              uid: response.data,
              trigger: 'download-schema'
            })
          })
          .catch((e) => {
            console.log(e);
            this.error = true;
          });
    }
  }
}
</script>

<style scoped lang="scss">
.measurements-menu {
  display: flex;
  flex-direction: row;
  padding: 17px 22px;
  width: 100%;
  //width: 819px;
  //height: 260px;
  background-color: #FAFAFA;
  /* box-shadow: 1px 1px 2px 0 rgba(0, 0, 0, 0.16); */
  border-bottom: 3px solid #F0F0F0;
  border-right: 3px solid #F0F0F0;

  &.mobile {
    flex-direction: column;

    .half.left {
      min-width: unset;
      width: 100%;

      .image-container {
        height: auto;
      }
    }

    .half.right {
      width: 100%;
      margin-top: 24px;
      padding-left: 0;

      .push-specs,
      .pull-specs {
        flex-wrap: wrap;
        justify-content: flex-start;
        align-items: flex-start;
        max-height: none;
        width: 100%;
        min-width: unset;

        .spec {
          width: 50%;
        }
      }
    }
  }

  .half {
    width: 50%;

    .heading {
      font-family: neue-haas-grotesk-display, sans-serif;
      font-weight: 700;
      font-size: 12px;
      display: flex;
      margin-bottom: 4px;
    }

    &.left {
      min-width: 680px;

      .image-container {
        position: relative;
        mix-blend-mode: multiply;
        height: 260px;

        #cilinder-image {
          object-fit: contain;
          width: 100%;
          max-height: 262px;
          height: 100%;
        }
      }

      .download {
        margin-top: 20px;
        background-color: #3db0c7;
        transition: all ease 0.3s;
        padding: 4px 6px;
        color: white;
        font-weight: 700;
        font-size: 12px;
        
        &.disabled {
          cursor: not-allowed;

          &:hover {
            background-color: gray;
          }
        }

        &:hover {
          background-color: darken(#3db0c7, 10%);
        }
      }

      .error {
        width: 230px;
        margin-top: 4px;
        border-radius: 5px;
        text-align: center;
        font-style: italic;
      }
    }

    &.right {
      max-width: 400px;
      padding-left: 20px;
      display: flex;
      flex-direction: column;
      justify-content: center;

      .push-specs, .pull-specs {
        min-width: 360px;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        max-height: 25px;
        margin-bottom: 7px;

        img {
          max-width: 25px;
          max-height: 21px;
          margin-right: 2px;

          &:last-child {
            margin-left: 2px;
          }
        }

        .spec {
          .spec-value {
            flex-shrink: 1;
            font-size: 16px;
            font-family: neue-haas-grotesk-display, sans-serif;
            font-weight: 700;
            color: #3DB0C7;
            transition: all ease 0.2s;
          }
        }
      }

      .cilinder-img {
        margin-bottom: 11px;
        position: relative;

        .cilinder-render {
          max-height: 100px;
          width: 100%;
          object-fit: cover;
          display: flex;
          flex-shrink: 1;
          mix-blend-mode: multiply;
        }

        .arrow {
          position: absolute;
          right: 50px;
          top: 22px;

          &.left {
            top: 50px;
            transform: rotate(180deg);
          }
        }
      }
    }
  }
}
</style>
